/** Browser analytics-consent persistence, kept independent from authentication. */
import { z } from "zod"

import { host } from "../host"

/** Bump when the analytics provider, purpose, or data categories change. */
export const ANALYTICS_CONSENT_POLICY_VERSION = "2026-08-27"
const CONSENT_KEY = "anthra.analytics_consent"

const analyticsConsentRecordSchema = z.object({
	analytics: z.boolean(),
	policyVersion: z.string(),
	decidedAt: z.string().refine(value => !Number.isNaN(Date.parse(value))),
})

/** The persisted visitor choice; it deliberately contains no account identifier. */
export type AnalyticsConsentRecord = z.infer<typeof analyticsConsentRecordSchema>

/** Restores a current, valid decision; malformed and stale values are treated as undecided. */
export async function restoreAnalyticsConsent(): Promise<AnalyticsConsentRecord | undefined> {
	const raw = await host().storage.get(CONSENT_KEY)
	if (!raw) return undefined
	try {
		const result = analyticsConsentRecordSchema.safeParse(JSON.parse(raw))
		if (!result.success || result.data.policyVersion !== ANALYTICS_CONSENT_POLICY_VERSION) {
			return undefined
		}
		return result.data
	} catch {
		return undefined
	}
}

/** Persists a visitor's explicit analytics choice independently of their session. */
export async function saveAnalyticsConsent(analytics: boolean): Promise<AnalyticsConsentRecord> {
	const record: AnalyticsConsentRecord = {
		analytics,
		policyVersion: ANALYTICS_CONSENT_POLICY_VERSION,
		decidedAt: new Date().toISOString(),
	}
	await host().storage.set(CONSENT_KEY, JSON.stringify(record))
	return record
}
